//client/src/services/api.js

// Dynamically determine API URL based on current host
function getApiUrl() {
  // If explicitly set in env, use that
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // In production (same domain), use relative path
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  
  // In development, use same hostname but port 3001
  const { protocol, hostname } = window.location;
  return `${protocol}//${hostname}:3001/api`;
}

const API_URL = getApiUrl();

// ─── Session continuity (silent refresh) ──────────────────
//
// Active users should never be logged out mid-task. To make that true, any
// authenticated request that comes back 401 first tries to silently refresh
// the token (POST /auth/refresh) and then retries the original request once.
// Only if that refresh fails do we treat the session as truly over.
//
// refreshPromise is a module-level single-flight guard: if several requests
// 401 at the same moment (e.g. a page firing three fetches on load), they all
// await the SAME refresh call instead of stampeding the refresh endpoint.
let refreshPromise = null;

// Optional hook the auth layer registers to react to a terminal auth failure
// (refresh failed → session is over). AuthContext sets this to perform the
// full-reset logout. If nothing is registered, api still clears the token so
// the app can't keep using a dead one.
let onAuthFailure = null;

class ApiService {
  constructor() {
    this.baseUrl = API_URL;
  }

  getToken() {
    return localStorage.getItem('token');
  }

  setToken(token) {
    localStorage.setItem('token', token);
  }

  clearToken() {
    localStorage.removeItem('token');
  }

  // AuthContext registers its logout/reset handler here.
  setAuthFailureHandler(fn) {
    onAuthFailure = fn;
  }

  headers(includeAuth = true) {
    const h = { 'Content-Type': 'application/json' };
    if (includeAuth) {
      const token = this.getToken();
      if (token) h['Authorization'] = `Bearer ${token}`;
    }
    return h;
  }

  // Single-flight token refresh. Returns the refresh payload ({ token, user })
  // on success (and stores the new token), or null on any failure. Safe to
  // call from many places at once — concurrent callers share one request.
  async refresh() {
    const token = this.getToken();
    if (!token) return null;

    if (!refreshPromise) {
      refreshPromise = fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
        .then(async (res) => {
          if (!res.ok) return null;
          const data = await res.json().catch(() => null);
          if (data?.token) {
            this.setToken(data.token);
            return data; // { token, user }
          }
          return null;
        })
        .catch(() => null)
        .finally(() => {
          // Clear the guard so the next 401 (later) can refresh again.
          refreshPromise = null;
        });
    }

    return refreshPromise;
  }

  // Terminal auth failure: refresh didn't work, so the session is over.
  // Always clear the dead token; hand off the UX (full reset / redirect) to
  // the registered handler if there is one.
  handleAuthFailure() {
    this.clearToken();
    if (typeof onAuthFailure === 'function') {
      onAuthFailure();
    }
  }

  async request(method, path, body = null, auth = true, _retried = false) {
    const opts = { method, headers: this.headers(auth) };
    if (body) opts.body = JSON.stringify(body);

    const res = await fetch(`${this.baseUrl}${path}`, opts);

    // ─── Global 401 backstop ──────────────────────────────
    // Only for authenticated requests, and never for the refresh endpoint
    // itself (that would loop). Public calls like /auth/login legitimately
    // return 401 for bad credentials and must pass straight through to the
    // caller — they use auth=false, so they skip this block.
    if (res.status === 401 && auth && !_retried && path !== '/auth/refresh') {
      const refreshed = await this.refresh();
      if (refreshed?.token) {
        // Got a fresh token — retry the original request exactly once.
        return this.request(method, path, body, auth, true);
      }
      // Refresh failed → session is genuinely over.
      this.handleAuthFailure();
      const data = await res.json().catch(() => ({}));
      const error = new Error(data.error || 'Session expired');
      error.status = 401;
      error.data = data;
      throw error;
    }

    const data = await res.json();

    if (!res.ok) {
      const error = new Error(data.error || 'Request failed');
      error.status = res.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  // File upload method (FormData - no Content-Type header, browser sets it with boundary)
  async upload(path, formData, _retried = false) {
    const headers = {};
    const token = this.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    // Same 401 backstop as request(): refresh once, retry, else tear down.
    if (res.status === 401 && !_retried) {
      const refreshed = await this.refresh();
      if (refreshed?.token) {
        return this.upload(path, formData, true);
      }
      this.handleAuthFailure();
      const data = await res.json().catch(() => ({}));
      const error = new Error(data.error || 'Session expired');
      error.status = 401;
      error.data = data;
      throw error;
    }

    const data = await res.json();

    if (!res.ok) {
      const error = new Error(data.error || 'Upload failed');
      error.status = res.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  get(path, auth = true) { return this.request('GET', path, null, auth); }
  post(path, body, auth = true) { return this.request('POST', path, body, auth); }
  put(path, body, auth = true) { return this.request('PUT', path, body, auth); }
  del(path, auth = true) { return this.request('DELETE', path, null, auth); }
}

const api = new ApiService();
export default api;